import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaperPlaneTilt, Wrench, CheckCircle, Circle, Copy, Trash, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TRADES = ["Electrical","Plumbing","HVAC","Concrete","Framing","Roofing","Drywall","Flooring","Fire Protection","Sitework","Landscaping","Signage"];
const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US");
const empty = { trade: "Electrical", company: "", email: "", scope: "", bid_due_date: "", message: "" };

export default function SubcontractorsTab({ projectId }) {
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/sub-invitations`).then(({ data }) => setInvites(data.invitations)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/sub-invitations`, form);
      toast.success(data.email_result?.status === "sent" ? "Invitation email sent" : "Sub invited (email not configured — link ready)");
      setOpen(false); setForm(empty); await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/sub-invite/${token}`);
    toast.success("Sub invite link copied");
  };
  const remove = async (id) => { await api.delete(`/projects/${projectId}/sub-invitations/${id}`); toast.success("Removed"); await load(); };
  const downloadFile = (fileId) => {
    const token = localStorage.getItem("ch_token");
    window.open(`${API}/sub-bid-files/${fileId}/download?auth=${token}`, "_blank");
  };

  const withBids = invites.filter((i) => i.bid);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-primary mb-1">Phase 4 · Subcontractor Bidding</div>
          <h2 className="font-heading font-bold text-xl tracking-tight">Send Plans to Subs</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-sub-button" className="rounded-none bg-primary hover:bg-primary/90 text-white gap-2">
              <PaperPlaneTilt size={18} weight="bold" /> Invite Sub
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Invite Subcontractor</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="sub-invite-form">
              <div className="space-y-2">
                <Label className="overline">Trade *</Label>
                <Select value={form.trade} onValueChange={(v) => setForm({ ...form, trade: v })}>
                  <SelectTrigger data-testid="sub-trade-select" className="rounded-none h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label className="overline">Company</Label><Input value={form.company} onChange={set("company")} data-testid="sub-company-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Email *</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="sub-email-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Scope Package</Label><Textarea value={form.scope} onChange={set("scope")} data-testid="sub-scope-input" className="rounded-none" rows={2} /></div>
              <div className="space-y-2"><Label className="overline">Bid Due Date</Label><Input type="date" value={form.bid_due_date} onChange={set("bid_due_date")} data-testid="sub-duedate-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Message</Label><Textarea value={form.message} onChange={set("message")} data-testid="sub-message-input" className="rounded-none" rows={2} /></div>
              <Button type="submit" disabled={busy} data-testid="sub-invite-submit" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                {busy ? "Sending…" : "Send Invitation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {invites.length ? (
        <div className="border border-border bg-card mb-8">
          <div className="overline text-muted-foreground px-4 pt-4">Invited Subcontractors</div>
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Trade</TableHead><TableHead>Company / Email</TableHead><TableHead>Due</TableHead>
              <TableHead>Opened</TableHead><TableHead>Status</TableHead><TableHead>Link</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invites.map((inv) => (
                <TableRow key={inv.id} className="even:bg-muted/20" data-testid={`sub-invite-row-${inv.id}`}>
                  <TableCell className="font-medium">{inv.trade}</TableCell>
                  <TableCell className="text-muted-foreground">{inv.company || "—"}<div className="text-xs">{inv.email}</div></TableCell>
                  <TableCell className="text-muted-foreground">{inv.bid_due_date || "—"}</TableCell>
                  <TableCell>{inv.opened ? <CheckCircle size={18} weight="fill" className="text-primary" /> : <Circle size={18} className="text-muted-foreground" />}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 bg-secondary">{inv.status}</span></TableCell>
                  <TableCell><button onClick={() => copyLink(inv.token)} data-testid={`sub-copy-link-${inv.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-150"><Copy size={16} /></button></TableCell>
                  <TableCell><button onClick={() => remove(inv.id)} data-testid={`sub-remove-${inv.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={16} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-card border border-border border-dashed p-12 text-center mb-8">
          <Wrench size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="text-muted-foreground">No subs invited yet. Invite subcontractors by trade — they'll get a link to enter their details and upload bids.</p>
        </div>
      )}

      {withBids.length > 0 && (
        <div>
          <div className="overline text-muted-foreground mb-2">Bid Leveling</div>
          <div className="border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Trade</TableHead><TableHead>Subcontractor</TableHead><TableHead className="text-right">Pricing</TableHead>
                <TableHead>Exclusions</TableHead><TableHead>Alternates</TableHead><TableHead>Files</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {withBids.map((inv) => (
                  <TableRow key={inv.id} className="even:bg-muted/20" data-testid={`sub-bid-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.trade}</TableCell>
                    <TableCell>{inv.bid.sub_name}<div className="text-xs text-muted-foreground">{inv.bid.company} · {inv.bid.address}</div></TableCell>
                    <TableCell className="text-right font-heading font-bold">{fmtMoney(inv.bid.pricing)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{inv.bid.exclusions || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{inv.bid.alternate_pricing || "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {(inv.bid.files || []).length ? inv.bid.files.map((f) => (
                          <button key={f.id} onClick={() => downloadFile(f.id)} data-testid={`sub-file-${f.id}`} className="text-primary hover:underline text-xs flex items-center gap-1 max-w-[160px] truncate"><DownloadSimple size={14} />{f.original_filename}</button>
                        )) : <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
